import React, { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from './ui'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  showDetails: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    this.setState({
      error,
      errorInfo
    })

    // Call the onError prop if provided
    this.props.onError?.(error, errorInfo)

    // Send error to analytics in production
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error tracking service
      console.error('Production Error:', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      })
    }
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/'
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    })
  }

  private toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }))
  }

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              ¡Ups! Algo salió mal
            </h1>

            {/* Error Description */}
            <p className="text-gray-600 mb-6 leading-relaxed">
              Ha ocurrido un error inesperado. No te preocupes, nuestro equipo ha sido notificado 
              y estamos trabajando para solucionarlo.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3 mb-6">
              <Button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                aria-label="Intentar de nuevo"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Intentar de nuevo
              </Button>

              <div className="flex gap-3">
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex-1"
                  aria-label="Ir al inicio"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Inicio
                </Button>

                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  className="flex-1"
                  aria-label="Recargar página"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Recargar
                </Button>
              </div>
            </div>

            {/* Error Details Toggle - Only in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="border-t pt-4">
                <button
                  onClick={this.toggleDetails}
                  className="flex items-center justify-center w-full text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
                  aria-expanded={this.state.showDetails}
                  aria-label={this.state.showDetails ? 'Ocultar detalles técnicos' : 'Mostrar detalles técnicos'}
                >
                  Detalles técnicos
                  {this.state.showDetails ? (
                    <ChevronUp className="w-4 h-4 ml-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 ml-1" />
                  )}
                </button>

                {this.state.showDetails && (
                  <div 
                    className="mt-4 p-4 bg-gray-50 rounded-lg text-left"
                    role="region"
                    aria-label="Detalles del error"
                  >
                    <div className="text-sm">
                      <div className="font-semibold text-red-600 mb-2">
                        Error: {this.state.error.message}
                      </div>
                      <div className="text-gray-700 text-xs font-mono whitespace-pre-wrap max-h-32 overflow-y-auto">
                        {this.state.error.stack}
                      </div>
                      {this.state.errorInfo?.componentStack && (
                        <details className="mt-2">
                          <summary className="text-gray-600 cursor-pointer">
                            Component Stack
                          </summary>
                          <div className="text-xs font-mono text-gray-500 whitespace-pre-wrap mt-1 max-h-32 overflow-y-auto">
                            {this.state.errorInfo.componentStack}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Support Info */}
            <div className="text-xs text-gray-500 mt-4">
              Si el problema persiste, contacta con{' '}
              <a 
                href="mailto:soporte@wilddraw.es" 
                className="text-blue-600 hover:text-blue-700 underline"
                aria-label="Enviar email a soporte"
              >
                soporte@wilddraw.es
              </a>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook version for functional components
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`
  
  return WrappedComponent
}

export default ErrorBoundary